import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, FlexColumn, Spinner, Suspense, Text, showSuccessToast } from 'core/components';
import { Field, FormActions, FormComponent, Switch } from 'core/form';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';
import api from 'core/util/api';
import DeviceCommandACL from './DeviceCommandACL';

const COMMAND_ACCESS_FIELDS = {
  deviceConfigurationToolEnabled: {
    helpText:
      'Allow AI Advisor to access device configurations and their history of changes. Configs and diffs in Device Details do not require AI Advisor.',
    defaultValue: true
  },
  deviceCommandToolEnabled: {
    label: 'Device Command Tool',
    helpText: 'Empower AI Advisor to securely run read-only "show" commands for immediate diagnostic context.',
    defaultValue: false
  },
  'deviceCommandAcls.aclMode': {
    label: 'ACL Mode',
    defaultValue: 'allow_all'
  },
  'deviceCommandAcls.rules': {
    label: 'Rules',
    defaultValue: [],
    isComplexArray: true
  },
  'deviceCommandAcls.rules[].matchType': {
    label: 'Match Type',
    rules: 'required',
    defaultValue: 'exact'
  },
  'deviceCommandAcls.rules[].pattern': {
    label: 'Pattern',
    rules: 'required',
    defaultValue: ''
  }
};

@inject('$auth', '$moduleConfig')
@observer
class CommandAccess extends Component {
  state = { loading: true };

  onLoad = (form) => {
    const { $moduleConfig } = this.props;
    Promise.all([$moduleConfig.fetchModuleConfig('ai'), api.get('/api/ui/devices/commands/acls')])
      .then(([config, aclResponse]) => {
        const { deviceCommandAcls } = aclResponse;
        form.init({
          deviceConfigurationToolEnabled: config.deviceConfigurationToolEnabled ?? true,
          deviceCommandToolEnabled: config.deviceCommandToolEnabled ?? false,
          deviceCommandAcls
        });
        this.setState({ loading: false });
      })
      // api.get/fetchModuleConfig already show their own error toast; just stop the spinner.
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  handleSubmit = (form, values) => {
    const { $moduleConfig } = this.props;
    const { deviceCommandAcls, ...moduleConfigFields } = values;

    const moduleConfigDirty = ['deviceConfigurationToolEnabled', 'deviceCommandToolEnabled'].some(
      (f) => form.getField(f).dirty
    );
    const aclDirty = ['deviceCommandAcls.aclMode', 'deviceCommandAcls.rules'].some((f) => form.getField(f).dirty);

    const fetchIfNeeded = moduleConfigDirty
      ? $moduleConfig.fetchModuleConfig('ai', { force: true })
      : Promise.resolve(null);

    return fetchIfNeeded
      .then((existingSettings) =>
        Promise.all(
          [
            moduleConfigDirty &&
              $moduleConfig.saveModuleConfig({
                module: 'ai',
                settings: { ...existingSettings, ...moduleConfigFields }
              }),
            aclDirty && api.put('/api/ui/devices/commands/acls', { data: { deviceCommandAcls } })
          ].filter(Boolean)
        )
      )
      .then(() => {
        form.init(values);
        showSuccessToast('Updated Kentik AI settings successfully');
      });
  };

  render() {
    const { $auth } = this.props;
    const { loading } = this.state;

    return (
      <FormComponent fields={COMMAND_ACCESS_FIELDS} options={{ name: 'commandAccess' }} onLoad={this.onLoad}>
        {({ form }) => {
          const deviceCommandToolEnabled = form?.getField('deviceCommandToolEnabled')?.value;
          // We only show this page to admin or super-admin, but want to prevent non-super-admin from changing these settings.
          const isAdminUser = $auth.isPreciselyAdministrator;
          // Security measure: prevent spoofers from enabling command access unless the company has the permission enabled.
          const allowSpoofedCommands = $auth.hasPermission('recon.allowSpoofedCommands', { overrideForSudo: false });
          const commandAccessDisabled =
            isAdminUser || (!deviceCommandToolEnabled && $auth.isSpoofed && !allowSpoofedCommands);

          return (
            <Suspense fallback={<Spinner />} loading={loading}>
              <FlexColumn p={4}>
                <Flex justifyContent="space-between" alignItems="center" mb={2}>
                  <SettingsPanelHeading level={2} mb={0}>
                    Command Access
                  </SettingsPanelHeading>
                  {form && (
                    <FormActions
                      form={form}
                      onSubmit={this.handleSubmit}
                      showCancel={false}
                      closeAfterSave={false}
                      submitButtonProps={{ disabled: !form.dirty || !form.valid }}
                    />
                  )}
                </Flex>
                <Text mb={3}>
                  Securely expand Kentik AI Advisor&apos;s troubleshooting capabilities by enabling SSH command access
                  to your devices. This requires a Kentik NMS device license.
                </Text>

                <FlexColumn gap={4} maxWidth="800px">
                  <Field name="deviceConfigurationToolEnabled" large mr={4} showLabel={false} disabled={isAdminUser}>
                    <Switch large switchLabel="Enable Configuration Access" />
                  </Field>

                  <Field
                    name="deviceCommandToolEnabled"
                    large
                    mr={4}
                    showLabel={false}
                    disabled={commandAccessDisabled}
                  >
                    <Switch large switchLabel="Enable Read-Only Diagnostic Commands" />
                  </Field>
                </FlexColumn>
                {deviceCommandToolEnabled && (
                  <FlexColumn mt={4} p={0}>
                    <DeviceCommandACL form={form} disabled={isAdminUser} />
                  </FlexColumn>
                )}
              </FlexColumn>
            </Suspense>
          );
        }}
      </FormComponent>
    );
  }
}

export default CommandAccess;
